import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPrEvents, getBusinessTransactions, getComponents } from '../../shared/api/api';
import StatusBadge from '../../shared/components/StatusBadge';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { getDisplayName } from '../../shared/auth/session';
import './DashboardPage.css';

/**
 * Dashboard — first screen after login.
 * Shows a stats carousel, PR summary, transaction summary,
 * and recent activity tables.
 */
function DashboardPage() {
  const navigate = useNavigate();
  const [prEvents, setPrEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [carouselIndex, setCarouselIndex] = useState(0);

  const username = getDisplayName() || 'Developer';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [prRes, componentsRes] = await Promise.allSettled([
        getPrEvents(),
        getComponents(),
      ]);

      if (prRes.status === 'fulfilled') {
        const d = prRes.value.data;
        setPrEvents(Array.isArray(d) ? d : d?.prEvents || d?.content || []);
      }

      const componentNames = componentsRes.status === 'fulfilled'
        ? (Array.isArray(componentsRes.value.data) ? componentsRes.value.data : [])
        : [];

      if (componentNames.length > 0) {
        const txResults = await Promise.allSettled(
          componentNames.map((name) => getBusinessTransactions('24h', name))
        );
        const allTransactions = txResults
          .filter((r) => r.status === 'fulfilled')
          .flatMap((r) => Array.isArray(r.value.data) ? r.value.data : []);
        setTransactions(allTransactions);
      }

      if (prRes.status === 'rejected' && componentsRes.status === 'rejected') {
        setError('Failed to load dashboard data.');
      }
    } catch {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  };

  // PR stats
  const prStats = useMemo(() => ({
    total: prEvents.length,
    passed: prEvents.filter((pr) => pr.status === 'PASSED').length,
    failed: prEvents.filter((pr) => pr.status === 'FAILED').length,
    pending: prEvents.filter((pr) => pr.status === 'PENDING').length,
  }), [prEvents]);

  // Transaction stats
  const txStats = useMemo(() => {
    const success = transactions.filter((tx) => tx.statusCode >= 200 && tx.statusCode < 300).length;
    const clientErr = transactions.filter((tx) => tx.statusCode >= 400 && tx.statusCode < 500).length;
    const serverErr = transactions.filter((tx) => tx.statusCode >= 500).length;
    const durations = transactions.map((tx) => tx.durationMs).filter(Boolean);
    const avgDuration = durations.length > 0
      ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length)
      : 0;
    const environments = [...new Set(transactions.map((tx) => tx.environment).filter(Boolean))];
    const components = [...new Set(transactions.map((tx) => tx.componentName).filter(Boolean))];
    const errorTransactions = transactions.filter((tx) => tx.statusCode >= 400);
    return {
      total: transactions.length,
      success,
      clientErr,
      serverErr,
      avgDuration,
      environments,
      components,
      errorRate: transactions.length > 0 ? ((clientErr + serverErr) / transactions.length * 100).toFixed(1) : '0.0',
      errorTransactions,
    };
  }, [transactions]);

  // Carousel slides
  const slides = useMemo(() => [
    {
      title: 'PR Events Overview',
      cards: [
        { label: 'Total PRs', value: prStats.total, accent: 'primary' },
        { label: 'Passed', value: prStats.passed, accent: 'success' },
        { label: 'Failed', value: prStats.failed, accent: 'error' },
        { label: 'Pending', value: prStats.pending, accent: 'warning' },
      ],
    },
    {
      title: 'Transaction Health',
      cards: [
        { label: 'Total Requests', value: txStats.total, accent: 'primary' },
        { label: 'Successful', value: txStats.success, accent: 'success' },
        { label: 'Client Errors', value: txStats.clientErr, accent: 'warning' },
        { label: 'Server Errors', value: txStats.serverErr, accent: 'error' },
      ],
    },
    {
      title: 'Performance & Coverage',
      cards: [
        { label: 'Avg Response', value: txStats.avgDuration > 0 ? `${txStats.avgDuration}ms` : '—', accent: 'info' },
        { label: 'Error Rate', value: `${txStats.errorRate}%`, accent: txStats.errorRate > 10 ? 'error' : 'success' },
        { label: 'Components', value: txStats.components.length, accent: 'primary' },
        { label: 'Environments', value: txStats.environments.length, accent: 'info' },
      ],
    },
  ], [prStats, txStats]);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % slides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = useCallback((i) => setCarouselIndex(i), []);

  // Recent items
  const recentPrs = useMemo(
    () => [...prEvents].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5),
    [prEvents]
  );

  const recentErrors = useMemo(
    () => txStats.errorTransactions
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 5),
    [txStats.errorTransactions]
  );

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading dashboard..." />}
      <h1 className="page-title">{getGreeting()}, {username}</h1>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats carousel */}
      <div className="carousel">
        <div className="carousel-header">
          <h2 className="carousel-title">{slides[carouselIndex].title}</h2>
          <div className="carousel-controls">
            <button
              className="carousel-arrow"
              onClick={() => setCarouselIndex((i) => (i - 1 + slides.length) % slides.length)}
              aria-label="Previous slide"
            >
              ‹
            </button>
            <div className="carousel-dots">
              {slides.map((_, i) => (
                <button
                  key={i}
                  className={`carousel-dot ${i === carouselIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
            <button
              className="carousel-arrow"
              onClick={() => setCarouselIndex((i) => (i + 1) % slides.length)}
              aria-label="Next slide"
            >
              ›
            </button>
          </div>
        </div>

        <div className="carousel-track">
          <div
            className="carousel-slides"
            style={{ transform: `translateX(-${carouselIndex * 100}%)` }}
          >
            {slides.map((slide, si) => (
              <div className="carousel-slide" key={si}>
                <div className="stats-grid">
                  {slide.cards.map((card) => (
                    <div className={`stat-card stat-card--${card.accent}`} key={card.label}>
                      <span className="stat-value">{card.value}</span>
                      <span className="stat-label">{card.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Two-column layout for recent activity */}
      <div className="dashboard-grid">
        {/* Recent PR Events */}
        <div className="card">
          <div className="dashboard-table-header">
            <h2 className="section-title">Recent PR Events</h2>
            <button className="btn btn-secondary" onClick={() => navigate('/pr-events')}>
              View All
            </button>
          </div>

          {recentPrs.length === 0 ? (
            <p className="empty-state">No PR events found.</p>
          ) : (
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Repository</th>
                  <th>PR #</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentPrs.map((pr) => (
                  <tr key={pr.trackingId}>
                    <td>{pr.repository}</td>
                    <td>#{pr.prNumber}</td>
                    <td><StatusBadge status={pr.status} /></td>
                    <td>{new Date(pr.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/pr-events/${pr.trackingId}`)}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Recent Errors */}
        <div className="card">
          <div className="dashboard-table-header">
            <h2 className="section-title">Recent Errors</h2>
            <button className="btn btn-secondary" onClick={() => navigate('/business-transactions')}>
              View All
            </button>
          </div>

          {recentErrors.length === 0 ? (
            <p className="empty-state">No errors recorded.</p>
          ) : (
            <table className="pr-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Duration</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recentErrors.map((tx) => (
                  <tr key={tx.id}>
                    <td>{tx.componentName || '—'}</td>
                    <td className="dash-endpoint">{tx.endpoint || '—'}</td>
                    <td>
                      <span className={`dash-status dash-status--${tx.statusCode >= 500 ? 'server' : 'client'}`}>
                        {tx.statusCode}
                      </span>
                    </td>
                    <td>{tx.durationMs != null ? `${tx.durationMs}ms` : '—'}</td>
                    <td>
                      <button
                        className="btn-link"
                        onClick={() => navigate(`/business-transactions/${tx.id}`)}
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Component health breakdown */}
      {txStats.components.length > 0 && (
        <div className="card" style={{ marginTop: 24 }}>
          <h2 className="section-title">Component Health</h2>
          <div className="component-health-grid">
            {txStats.components.map((comp) => {
              const compTxs = transactions.filter((tx) => tx.componentName === comp);
              const compErrors = compTxs.filter((tx) => tx.statusCode >= 400).length;
              const compSuccess = compTxs.filter((tx) => tx.statusCode >= 200 && tx.statusCode < 300).length;
              const compAvg = compTxs.length > 0
                ? Math.round(compTxs.map((tx) => tx.durationMs || 0).reduce((a, b) => a + b, 0) / compTxs.length)
                : 0;
              const healthPct = compTxs.length > 0 ? Math.round((compSuccess / compTxs.length) * 100) : 100;

              return (
                <div className="component-card" key={comp}>
                  <div className="component-card-header">
                    <span className="component-card-name">{comp}</span>
                    <span className={`component-card-health ${healthPct >= 95 ? 'healthy' : healthPct >= 80 ? 'degraded' : 'unhealthy'}`}>
                      {healthPct}%
                    </span>
                  </div>
                  <div className="component-card-bar">
                    <div
                      className={`component-card-bar-fill ${healthPct >= 95 ? 'healthy' : healthPct >= 80 ? 'degraded' : 'unhealthy'}`}
                      style={{ width: `${healthPct}%` }}
                    />
                  </div>
                  <div className="component-card-stats">
                    <span>{compTxs.length} requests</span>
                    <span>{compErrors} errors</span>
                    <span>{compAvg}ms avg</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
